import React, {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BlurView } from 'expo-blur';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import Animated, { useAnimatedStyle, withTiming } from 'react-native-reanimated';

import { BodyText, GlassView, Icon, theme, useNavbarContainerHeight } from '@sg/garnish';

import { useTrackEffect } from '@order/Telemetry';

export const FlexEntryPoint = (props: FlexEntryPointProps) => {
  const { isLiquidGlassEnabled = false, onCheckEligibility, onDismiss } = props;
  const { formatMessage } = useIntl();
  const { offsetStyle } = useNavbarContainerHeight();

  useTrackEffect('home.hsafsa_campaign_banner.view');

  // ─── Refs ────────────────────────────────────────────────────────────

  const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // ─── State ───────────────────────────────────────────────────────────

  const [isVisible, setIsVisible] = useState(false);

  // ─── Derived ─────────────────────────────────────────────────────────

  // Glass cannot be faded through `opacity` (it stops rendering at 0), so the
  // surface is faded natively by animating its style between `none` and `regular`.
  const glassEffectStyle = useMemo<GlassEffectStyle>(
    () => ({
      style: isVisible ? 'regular' : 'none',
      animate: true,
      animationDuration: FADE_DURATION_SECONDS,
    }),
    [isVisible],
  );

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
    dismissTimeoutRef.current = setTimeout(onDismiss, FADE_DURATION);
  }, [onDismiss]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsVisible(true));

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(dismissTimeoutRef.current);
    };
  }, []);

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [styles.container, styles.absoluteWrapper, offsetStyle];
  const backgroundStyles = isLiquidGlassEnabled ? styles.glassBackground : styles.background;

  const contentAnimatedStyles = useAnimatedStyle(
    () => ({ opacity: withTiming(isVisible ? 1 : 0, { duration: FADE_DURATION }) }),
    [isVisible],
  );
  const contentContainerStyles = [styles.contentContainer, contentAnimatedStyles];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      {isLiquidGlassEnabled ? (
        <GlassView glassEffectStyle={glassEffectStyle} style={styles.glass} />
      ) : null}

      <Animated.View style={contentContainerStyles}>
        {isLiquidGlassEnabled ? null : (
          <BlurView
            intensity={80}
            tint="light"
            pointerEvents="none"
            style={StyleSheet.absoluteFill}
          />
        )}

        <View style={backgroundStyles} />

        <TouchableOpacity
          role="link"
          aria-label={formatMessage(messages.check)}
          style={styles.content}
          onPress={onCheckEligibility}
        >
          <View style={styles.flexIcon}>
            <Icon name="IconFlex" color={theme.colors.WHITE} width={36} height={16} />
          </View>

          <View style={styles.info}>
            <BodyText sizeMatch={['14']} style={styles.title} bold>
              <FormattedMessage {...messages.title} />
            </BodyText>

            <BodyText sizeMatch={['12']} style={styles.description}>
              <FormattedMessage {...messages.description} />
            </BodyText>
          </View>
        </TouchableOpacity>

        <TouchableOpacity
          role="button"
          aria-label={formatMessage(messages.dismiss)}
          style={styles.closeIcon}
          onPress={handleDismiss}
        >
          <Icon name="IconClose" color={theme.colors.GRAY} width={16} height={16} />
        </TouchableOpacity>
      </Animated.View>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Pay using your HSA / FSA',
    description: 'Flex Entry Point | Title',
  },
  description: {
    defaultMessage: 'Check if you qualify here',
    description: 'Flex Entry Point | Description',
  },
  check: {
    defaultMessage: 'Check eligibility',
    description: 'Flex Entry Point | Check',
  },
  dismiss: {
    defaultMessage: 'Dismiss',
    description: 'Flex Entry Point | Dismiss',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const FADE_DURATION = theme.transitions.base;
const FADE_DURATION_SECONDS = FADE_DURATION / 1000;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  absoluteWrapper: {
    position: 'absolute',
    zIndex: 1,
    bottom: 0,
    left: theme.spacing['4'],
    right: theme.spacing['4'],
  },
  container: {
    overflow: 'hidden',
    borderRadius: theme.radius['xxlarge'],
  },
  contentContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing['2'],
    paddingHorizontal: theme.spacing['6'],
    paddingVertical: theme.spacing['4'],
  },
  background: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
  },
  glassBackground: {
    ...StyleSheet.absoluteFill,
    backgroundColor: theme.colors.OPACITY.WHITE.DARKER,
  },
  glass: {
    ...StyleSheet.absoluteFill,
    pointerEvents: 'none',
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  info: {
    flex: 1,
    gap: theme.spacing['1'],
  },
  flexIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: 'rgba(11, 0, 119, 1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeIcon: {
    width: 24,
    height: 24,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: theme.colors.GRAY,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    color: theme.colors.CHARCOAL,
  },
  description: {
    color: theme.colors.CHARCOAL,
    textDecorationLine: 'underline',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GlassEffectStyle = ComponentProps<typeof GlassView>['glassEffectStyle'];

type FlexEntryPointProps = {
  isLiquidGlassEnabled?: boolean;
  onCheckEligibility: () => void;
  onDismiss: () => void;
};
